import { Provenance, Panel } from '../features/agency/ui';
import { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { isApiError } from '@aiptyg/types';
import {
  useTaskDetail,
  updateTask,
  changeTaskStatus,
  appStoreLookup,
  ALL_TASK_CATEGORIES,
  ALL_TASK_TYPES,
  ALL_TASK_PLATFORMS,
  TASK_CATEGORY_LABELS,
  TASK_TYPE_LABELS,
  TASK_PLATFORM_LABELS,
  TASK_STATUS_LABELS,
  TASK_STATUS_TRANSITIONS,
  TASK_STATUS_ACTION_LABELS,
} from '../features/tasks';
import type { AnyTaskType, TaskStatus, TaskCategory, TaskPlatform, TaskStepDTO, DownloadAppInfoDTO, DownloadKeywordQuotaDTO } from '../features/tasks';
import { useDownloadStats, useTaskAssignments, DownloadStatsPanel, ASSIGNMENT_STATUS_LABELS } from '../features/assignments';
import type { AssignmentStatus } from '../features/assignments';
import { useToastStore } from '../store/toast.store';
import { ConfirmDialog } from '../components/feedback/ConfirmDialog';
import { TaskStepEditor } from '../features/tasks/components/TaskStepEditor';
import { RichTaskTextEditor } from '../features/tasks/components/RichTaskTextEditor';
import { MessageImportPanel } from '../features/tasks/components/MessageImportPanel';
import { MessagePoolStats } from '../features/tasks/components/MessagePoolStats';
import type { MessageImportBatchDTO, MessagePoolStatsDTO } from '../features/tasks/types/tasks.types';
import {
  buildLookupSearchKeywords,
  buildSearchKeywordQuotasFromKeywords,
  normalizeSearchKeywordQuotas,
  sumSearchKeywordTargetCounts,
} from '../features/tasks/utils/searchKeywords';
import { normalizeImageValueForSubmit, uploadImageToCos, resolveImagePreviewUrl } from '../lib/upload/cos';
import { useMarketConfigs } from '../features/settings';
import type { MarketConfigDTO } from '../features/settings/types/settings.types';

const STATUS_STYLES: Record<TaskStatus, string> = {
  draft: 'bg-gray-100 text-gray-600',
  active: 'bg-blue-50 text-blue-700',
  paused: 'bg-amber-50 text-amber-700',
  archived: 'bg-gray-100 text-gray-500',
};

interface FormState {
  title: string;
  description: string;
  description_title: string;
  category: string;
  task_type: string;
  market_id: string;
  platform: string;
  icon_url: string;
  tags: string;
  rules: string;
  rules_title: string;
  review_rules: string;
  review_rules_title: string;
  rewardYuan: string;
  max_assignments: string;
  completed_count: string;
  assignment_timeout_minutes: string;
  require_review: boolean;
  is_recommended: boolean;
  is_new: boolean;
  sort_order: string;
  start_at: string;
  end_at: string;
}

function isoToLocalDatetime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

function formatShortDt(iso: string): string {
  const d = new Date(iso);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${m}-${day} ${h}:${min}`;
}

function emptyAppInfo(): DownloadAppInfoDTO {
  return {
    id: 0, bundle_id: '', search_keywords: [], search_keyword_quotas: [], name: '', version: '', price: 0, formatted_price: '',
    artwork_url: '', developer_name: '', description: '', genre: '',
    file_size_bytes: 0, rating: 0, rating_count: 0, content_rating: '', screenshot_urls: [],
  };
}

function emptyKeywordQuota(): DownloadKeywordQuotaDTO {
  return { keyword: '', target_count: 1, completed_count: 0 };
}

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      task_not_found: '任务不存在',
      task_has_assignments: '任务已有用户领取，可改为归档移除',
      task_type_change_not_allowed: '任务已有领取记录，不能切换任务类型，请新建任务',
      invalid_request_body: '请求参数不合法',
      invalid_status_transition: '状态变更不合法',
      network_error: '网络连接失败',
    };
    return map[e.message] ?? `操作失败 (${e.message})`;
  }
  return '操作失败';
}

function resolveLookupError(e: unknown): string {
  if (!isApiError(e)) return '查询失败';
  const map: Record<string, string> = {
    app_store_app_not_found: '公开 App Store 未找到该应用，请核对 App ID 或切换地区',
    invalid_app_store_lookup_input: 'App ID、Bundle ID 或地区格式不正确',
    network_error: '网络连接失败',
  };
  return map[e.message] ?? `查询失败 (${e.message})`;
}

const inputCls = 'w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50';
const textareaCls = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y';
const selectCls = 'h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50';

function defaultMarketConfig(): MarketConfigDTO {
  return {
    market_id: 'CN',
    country_code: 'CN',
    name: '中国',
    locale: 'zh-CN',
    currency: 'CNY',
    currency_symbol: '¥',
    currency_exponent: 2,
    enabled: true,
    enabled_task_types: ['normal', 'other_normal', 'download', 'message_send'],
    enabled_features: {},
    requires_manual_payout: false,
    country_confirmation_required: false,
    task_configs: [],
  };
}

function parseRewardMinor(value: string, exponent: number): number {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (exponent <= 0) return Math.round(amount);
  return Math.round(amount * Math.pow(10, exponent));
}

function emptyStep(): TaskStepDTO {
  return { id: crypto.randomUUID(), order: 0, title: '', description: '', images: [], links: [] };
}

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const taskId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  const messageImportBatch = (location.state as { messageImportBatch?: MessageImportBatchDTO | null } | null)?.messageImportBatch ?? null;
  const { task, loading, error, reload, removeTask } = useTaskDetail(taskId);
  const { items: marketConfigs } = useMarketConfigs();
  const isDownloadTask = task?.task_type === 'download';
  const { stats: dlStats, loading: dlStatsLoading, error: dlStatsError, reload: dlStatsReload } = useDownloadStats(isDownloadTask ? taskId : undefined);
  const { items: recentAssignments, loading: assignmentsLoading, error: assignmentsError, reload: assignmentsReload } = useTaskAssignments(isDownloadTask ? taskId : undefined);
  const addToast = useToastStore((s) => s.addToast);

  const [form, setForm] = useState<FormState>({
    title: '', description: '', category: 'experience', task_type: 'normal', market_id: 'CN', platform: 'all',
    description_title: '', icon_url: '', tags: '', rules: '', rules_title: '', review_rules: '', review_rules_title: '',
    rewardYuan: '', max_assignments: '0', completed_count: '0', assignment_timeout_minutes: '2',
    require_review: true, is_recommended: false, is_new: false,
    sort_order: '0', start_at: '', end_at: '',
  });
  const [steps, setSteps] = useState<TaskStepDTO[]>([]);
  const [messageStatsRefreshKey, setMessageStatsRefreshKey] = useState(0);
  const [messagePoolStats, setMessagePoolStats] = useState<MessagePoolStatsDTO | null>(null);
  const [appInfo, setAppInfo] = useState<DownloadAppInfoDTO>(emptyAppInfo());
  const [lookupCountry, setLookupCountry] = useState('cn');
  const [lookingUp, setLookingUp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stepUploads, setStepUploads] = useState<Record<string, boolean>>({});
  const [iconUploading, setIconUploading] = useState(false);
  const [uploadedIconUrl, setUploadedIconUrl] = useState('');

  const selectedMarket = useMemo(
    () => marketConfigs.find((item) => item.market_id === form.market_id) ?? marketConfigs[0] ?? defaultMarketConfig(),
    [form.market_id, marketConfigs],
  );
  const enabledTaskTypes = selectedMarket.enabled_task_types.length > 0
    ? selectedMarket.enabled_task_types
    : ALL_TASK_TYPES;

  // Status change dialog
  const [statusTarget, setStatusTarget] = useState<TaskStatus | null>(null);
  const [statusChanging, setStatusChanging] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Populate form when task loads
  useEffect(() => {
    if (!task) return;
    setForm({
      title: task.title,
      description: task.description,
      description_title: task.description_title || '',
      category: task.category,
      task_type: task.task_type || 'normal',
      market_id: task.market_id || 'CN',
      platform: task.platform || 'all',
      icon_url: task.icon_url || '',
      tags: (task.tags ?? []).join(', '),
      rules: task.rules || '',
      rules_title: task.rules_title || '',
      review_rules: task.review_rules || '',
      review_rules_title: task.review_rules_title || '',
      rewardYuan: task.reward_amount_yuan,
      max_assignments: String(task.max_assignments),
      completed_count: String(task.completed_count ?? 0),
      assignment_timeout_minutes: String((task.assignment_timeout_seconds ?? 120) / 60),
      require_review: task.require_review,
      is_recommended: task.is_recommended ?? false,
      is_new: task.is_new ?? false,
      sort_order: String(task.sort_order),
      start_at: isoToLocalDatetime(task.start_at),
      end_at: isoToLocalDatetime(task.end_at),
    });
    setSteps((task.steps ?? []).map((step) => ({
      ...step,
      images: step.images ?? [],
    })));
    setAppInfo(
      task.app_info
        ? {
            ...emptyAppInfo(),
            ...task.app_info,
            search_keywords: task.app_info.search_keywords ?? [],
            search_keyword_quotas:
              task.app_info.search_keyword_quotas && task.app_info.search_keyword_quotas.length > 0
                ? task.app_info.search_keyword_quotas
                : buildSearchKeywordQuotasFromKeywords(task.app_info.search_keywords ?? []),
          }
        : emptyAppInfo(),
    );
  }, [task]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'market_id') {
        const nextMarket = marketConfigs.find((item) => item.market_id === value);
        const nextTaskTypes = nextMarket?.enabled_task_types?.length ? nextMarket.enabled_task_types : ALL_TASK_TYPES;
        if (!nextTaskTypes.includes(next.task_type)) {
          next.task_type = nextTaskTypes[0] ?? 'normal';
        }
      }
      if (key === 'task_type' && value === 'message_send') {
        next.platform = 'ios';
        next.require_review = true;
      }
      return next;
    });
  };

  const rewardMinor = parseRewardMinor(form.rewardYuan, selectedMarket.currency_exponent);
  const rewardFen = rewardMinor;
  const maxAssignments = parseInt(form.max_assignments, 10) || 0;
  const downloadMaxAssignments = sumSearchKeywordTargetCounts(
    normalizeSearchKeywordQuotas(appInfo.search_keyword_quotas ?? []),
  );
  const completedCount = parseInt(form.completed_count, 10) || 0;
  const assignmentTimeoutSeconds = Math.max(0, Math.round((parseFloat(form.assignment_timeout_minutes || '0') || 0) * 60));
  const sortOrder = parseInt(form.sort_order || '0', 10);
  const isMessageSend = form.task_type === 'message_send';
  const responsiveMessageGridClass = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3';
  const basicInfoGridClass = isMessageSend ? responsiveMessageGridClass : 'grid grid-cols-4 gap-4';
  const rewardGridClass = isMessageSend ? responsiveMessageGridClass : 'grid grid-cols-3 gap-4';
  const timeAndSortGridClass = isMessageSend ? responsiveMessageGridClass : 'grid grid-cols-2 gap-4';
  const hasSentMessageItem = (messagePoolStats?.sent ?? 0) > 0;
  const canSave =
    form.title.trim() !== '' &&
    form.category !== '' &&
    rewardMinor > 0 &&
    !saving;

  const handleSave = async () => {
    if (!canSave || Object.values(stepUploads).some(Boolean)) { if (Object.values(stepUploads).some(Boolean)) addToast("error", "请等待视频上传和处理完成后再保存"); return; }
    if (form.task_type === 'normal' && appInfo.search_keywords?.[0]?.trim() && (!Number.isSafeInteger(appInfo.id) || appInfo.id <= 0)) {
      addToast('error', '配置关键词时，请填写有效的 App Store 应用 ID');
      return;
    }
    setSaving(true);
    try {
      const tags = form.tags.split(',').map((t) => t.trim()).filter(Boolean);
      const searchKeywordQuotas = normalizeSearchKeywordQuotas(appInfo.search_keyword_quotas ?? []);
      const effectiveMaxAssignments = form.task_type === 'download'
        ? sumSearchKeywordTargetCounts(searchKeywordQuotas)
        : isMessageSend
          ? maxAssignments
        : maxAssignments;
      await updateTask(taskId, {
        title: form.title.trim(),
        description: form.description.trim(),
        description_title: form.description_title.trim(),
        category: form.category,
        task_type: form.task_type,
        platform: isMessageSend ? 'ios' : form.platform,
        market_id: selectedMarket.market_id,
        country_code: selectedMarket.country_code,
        currency: selectedMarket.currency,
        currency_symbol: selectedMarket.currency_symbol,
        currency_exponent: selectedMarket.currency_exponent,
        reward_amount_minor: rewardMinor,
        icon_url: normalizeImageValueForSubmit(form.icon_url.trim()),
        tags,
        rules: form.rules,
        rules_title: form.rules_title.trim(),
        review_rules: form.review_rules,
        review_rules_title: form.review_rules_title.trim(),
        reward_amount_fen: rewardFen,
        completed_count: completedCount,
        max_assignments: effectiveMaxAssignments,
        assignment_timeout_seconds: assignmentTimeoutSeconds,
        require_review: isMessageSend ? true : form.require_review,
        is_recommended: form.is_recommended,
        is_new: form.is_new,
        steps: steps.map((s, i) => ({
          ...s,
          order: i + 1,
          images: (s.images ?? []).map((image) => normalizeImageValueForSubmit(image)),
        })),
        app_info: form.task_type === 'download'
          ? {
              ...appInfo,
              search_keyword_quotas: searchKeywordQuotas,
              search_keywords: searchKeywordQuotas.map((item) => item.keyword),
            }
          : form.task_type === 'normal' && appInfo.search_keywords?.[0]?.trim()
            ? { ...emptyAppInfo(), id: appInfo.id, search_keywords: [appInfo.search_keywords[0].trim()] }
            : null,
        sort_order: sortOrder,
        start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
      });
      addToast('success', '保存成功');
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusConfirm = async () => {
    if (!statusTarget) return;
    setStatusChanging(true);
    try {
      await changeTaskStatus(taskId, statusTarget);
      addToast('success', `已${TASK_STATUS_ACTION_LABELS[statusTarget]}`);
      setStatusTarget(null);
      reload();
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setStatusChanging(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    const err = await removeTask();
    setDeleting(false);
    if (err) {
      addToast('error', err);
      setDeleteOpen(false);
    } else {
      addToast('success', '任务已删除；已有领取记录的任务会自动归档');
      navigate('/tasks');
    }
  };

  const currentStatus = task?.status as TaskStatus | undefined;
  const transitions = currentStatus ? (TASK_STATUS_TRANSITIONS[currentStatus] ?? []) : [];

  // --- Steps helpers ---
  const addStep = () => setSteps((prev) => [...prev, emptyStep()]);
  const removeStep = (idx: number) => setSteps((prev) => prev.filter((_, i) => i !== idx));
  const updateStep = (idx: number, patch: Partial<TaskStepDTO>) =>
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const moveStep = (idx: number, dir: -1 | 1) => {
    setSteps((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  // --- AppInfo helpers ---
  const setAppField = <K extends keyof DownloadAppInfoDTO>(key: K, value: DownloadAppInfoDTO[K]) => {
    setAppInfo((prev) => ({ ...prev, [key]: value }));
  };

  const addKeywordQuota = () => {
    setAppInfo((prev) => ({
      ...prev,
      search_keyword_quotas: [...(prev.search_keyword_quotas ?? []), emptyKeywordQuota()],
    }));
  };

  const updateKeywordQuota = (index: number, patch: Partial<DownloadKeywordQuotaDTO>) => {
    setAppInfo((prev) => ({
      ...prev,
      search_keyword_quotas: (prev.search_keyword_quotas ?? []).map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    }));
  };

  const removeKeywordQuota = (index: number) => {
    setAppInfo((prev) => ({
      ...prev,
      search_keyword_quotas: (prev.search_keyword_quotas ?? []).filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleLookup = async () => {
    const bundleId = appInfo.bundle_id.trim();
    const appId = appInfo.id;
    if (!bundleId && !appId) {
      addToast('error', '请先填写 App ID 或 Bundle ID');
      return;
    }
    setLookingUp(true);
    try {
      const result = await appStoreLookup({
        bundleId: appId ? undefined : bundleId || undefined,
        appId: appId || undefined,
        country: lookupCountry,
      });
      const currentQuotas = appInfo.search_keyword_quotas ?? [];
      const currentKeywords = currentQuotas.map((item) => item.keyword).filter(Boolean);
      const nextKeywords = currentKeywords.length > 0 ? currentKeywords : buildLookupSearchKeywords(result);
      const nextQuotas = currentQuotas.length > 0
        ? currentQuotas
        : buildSearchKeywordQuotasFromKeywords(nextKeywords);
      setAppInfo({ ...result, search_keywords: nextKeywords, search_keyword_quotas: nextQuotas });
      if (result.name) {
        setField('title', result.name);
      }
      if (result.artwork_url) {
        setField('icon_url', result.artwork_url);
      }
      addToast('success', nextKeywords.length > currentKeywords.length ? `应用信息查询成功，已生成 ${nextKeywords.length} 个搜索关键词` : '应用信息查询成功');
    } catch (e) {
      addToast('error', resolveLookupError(e));
    } finally {
      setLookingUp(false);
    }
  };

  const handleIconUpload = async (file: File | null) => {
    if (!file) return;
    try {
      setIconUploading(true);
      const result = await uploadImageToCos(file, 'task-icon');
      setUploadedIconUrl(result.readUrl);
      setField('icon_url', result.relativePath);
    } catch {
      addToast('error', '图标上传失败');
    } finally {
      setIconUploading(false);
    }
  };

  const isDownload = form.task_type === 'download';
  const setFieldFromMessagePoolStats = (stats: MessagePoolStatsDTO | null) => {
    setMessagePoolStats(stats);
    if (stats?.total === undefined) return;
    setForm((prev) => (
      prev.task_type === 'message_send'
        ? { ...prev, max_assignments: String(stats.total) }
        : prev
    ));
  };

  // Loading
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  // Error
  if (error || !task) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm text-red-500 mb-3">{error ?? '任务不存在'}</p>
        <Link to="/tasks" className="text-sm text-primary hover:underline">返回列表</Link>
      </div>
    );
  }

  if (task.agent_portal_task_id) return <div className="space-y-5 max-w-3xl"><Link className="text-primary" to="/tasks">返回任务列表</Link><Panel title={task.title}><Provenance item={task}/><p className="text-sm text-gray-600">此任务由商户发布，内容与上线状态须通过商户任务审核和控制流程管理。</p><p className="text-sm">当前状态：{TASK_STATUS_LABELS[task.status as TaskStatus] || task.status} · 奖励 {task.reward_display || task.reward_amount_yuan} · 名额 {task.max_assignments}</p><Link className="text-primary text-sm" to={`/agent-tasks/${task.agent_portal_task_id}`}>查看商户版本、差异与线上控制</Link></Panel></div>;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <Link to="/tasks" className="hover:text-primary transition-colors">任务管理</Link>
        <span>/</span>
        <span className="text-gray-900">任务详情</span>
      </div>

      {/* Status section */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-500">当前状态</span>
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
            STATUS_STYLES[currentStatus!] ?? 'bg-gray-100 text-gray-600'
          }`}
        >
          {TASK_STATUS_LABELS[currentStatus!] ?? task.status}
        </span>
        {transitions.map((target) => (
          <button
            key={target}
            onClick={() => setStatusTarget(target)}
            className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            {TASK_STATUS_ACTION_LABELS[target]}
          </button>
        ))}
      </div>

      {/* 基础信息 */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-gray-900">基础信息</h2>

        <div className={basicInfoGridClass}>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">市场 <span className="text-red-500">*</span></label>
            <select
              value={form.market_id}
              onChange={(e) => setField('market_id', e.target.value)}
              disabled={hasSentMessageItem}
              className={`${selectCls} disabled:bg-gray-50 disabled:text-gray-500`}
            >
              {marketConfigs.length === 0 ? (
                <option value="CN">CN · 中国</option>
              ) : marketConfigs.map((market) => (
                <option key={market.market_id} value={market.market_id}>{market.market_id} · {market.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">任务类型 <span className="text-red-500">*</span></label>
            <select
              value={form.task_type}
              onChange={(e) => setField('task_type', e.target.value)}
              disabled={hasSentMessageItem}
              className={`${selectCls} disabled:bg-gray-50 disabled:text-gray-500`}
            >
              {enabledTaskTypes.map((t) => (
                <option key={t} value={t}>{TASK_TYPE_LABELS[t as AnyTaskType] ?? t}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">平台 <span className="text-red-500">*</span></label>
            <select
              value={isMessageSend ? 'ios' : form.platform}
              onChange={(e) => setField('platform', e.target.value)}
              disabled={isMessageSend || hasSentMessageItem}
              className={`${selectCls} disabled:bg-gray-50 disabled:text-gray-500`}
            >
              {ALL_TASK_PLATFORMS.map((p) => (
                <option key={p} value={p}>{TASK_PLATFORM_LABELS[p as TaskPlatform]}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">分类 <span className="text-red-500">*</span></label>
            <select value={form.category} onChange={(e) => setField('category', e.target.value)} className={selectCls}>
              {ALL_TASK_CATEGORIES.map((c) => (
                <option key={c} value={c}>{TASK_CATEGORY_LABELS[c as TaskCategory]}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">任务标题 <span className="text-red-500">*</span></label>
          <input type="text" value={form.title} onChange={(e) => setField('title', e.target.value)} disabled={hasSentMessageItem} className={`${inputCls} disabled:bg-gray-50 disabled:text-gray-500`} />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">任务描述</label>
          <input type="text" value={form.description_title} onChange={(e) => setField('description_title', e.target.value)} disabled={hasSentMessageItem} placeholder={form.task_type === 'download' ? '默认: 任务要求' : '默认: 任务详情'} className={`${inputCls} disabled:bg-gray-50 disabled:text-gray-500`} />
          {hasSentMessageItem ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">已有发送遥测，任务描述暂不允许编辑，以保持审核快照上下文一致。</div>
          ) : (
            <RichTaskTextEditor value={form.description} onChange={(value) => setField('description', value)} placeholder="可选；不填写时客户端不展示该模块" />
          )}
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">图标 URL</label>
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_160px]">
            <input type="text" value={form.icon_url} onChange={(e) => setField('icon_url', e.target.value)} placeholder="https://... 或上传后自动生成" className={inputCls} />
            <label className="flex h-9 cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
              {iconUploading ? '上传中...' : '上传图标'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => void handleIconUpload(event.target.files?.[0] ?? null)}
              />
            </label>
          </div>
          {form.icon_url ? (
            <div className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
              <img
                src={resolveImagePreviewUrl(form.icon_url, uploadedIconUrl ? { [form.icon_url]: uploadedIconUrl } : undefined)}
                alt="任务图标预览"
                className="h-24 w-24 object-cover"
              />
            </div>
          ) : null}
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">标签</label>
          <input type="text" value={form.tags} onChange={(e) => setField('tags', e.target.value)} placeholder="逗号分隔" className={inputCls} />
          <p className="text-xs text-gray-400">多个标签用英文逗号分隔</p>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">任务规则</label>
          <input type="text" value={form.rules_title} onChange={(e) => setField('rules_title', e.target.value)} placeholder="默认: 任务规则" className={inputCls} />
          <RichTaskTextEditor value={form.rules} onChange={(value) => setField('rules', value)} placeholder="任务规则说明" />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">审核规则</label>
          <input type="text" value={form.review_rules_title} onChange={(e) => setField('review_rules_title', e.target.value)} placeholder="默认: 审核规则" className={inputCls} />
          <RichTaskTextEditor value={form.review_rules} onChange={(value) => setField('review_rules', value)} placeholder="审核判定规则" />
        </div>
      </section>

      {/* 奖励配置 */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-gray-900">奖励配置</h2>

        <div className={rewardGridClass}>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">奖励金额（{selectedMarket.currency_symbol} {selectedMarket.currency}）<span className="text-red-500">*</span></label>
            <input type="number" min="0" step={selectedMarket.currency_exponent > 0 ? '0.01' : '1'} value={form.rewardYuan} onChange={(e) => setField('rewardYuan', e.target.value)} className={`${inputCls} tabular-nums`} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">最大名额</label>
            <input
              type="number"
              min="0"
              step="1"
              value={form.task_type === 'download' ? String(downloadMaxAssignments) : form.max_assignments}
              onChange={(e) => setField('max_assignments', e.target.value)}
              disabled={form.task_type === 'download' || isMessageSend}
              className={`${inputCls} tabular-nums disabled:bg-gray-50 disabled:text-gray-500`}
            />
            <p className="text-xs text-gray-400">{form.task_type === 'download' ? '下载任务由关键词名额自动累加' : isMessageSend ? '短信任务由号码池导入后自动同步' : '0 表示不限名额'}</p>
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">已完成数</label>
            <input type="number" min="0" step="1" value={form.completed_count} onChange={(e) => setField('completed_count', e.target.value)} className={`${inputCls} tabular-nums`} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">接取后提交时限（分钟）</label>
            <input type="number" min="0" step="0.5" value={form.assignment_timeout_minutes} onChange={(e) => setField('assignment_timeout_minutes', e.target.value)} className={`${inputCls} tabular-nums`} />
            <p className="text-xs text-gray-400">0 表示不启用超时释放。</p>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-500">
          <span>已分配: {task.current_assigned}</span>
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={isMessageSend ? true : form.require_review}
              onChange={(e) => setField('require_review', e.target.checked)}
              disabled={isMessageSend || hasSentMessageItem}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50 disabled:opacity-50"
            />
            需要人工审核
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.is_recommended} onChange={(e) => setField('is_recommended', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50" />
            推荐
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.is_new} onChange={(e) => setField('is_new', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50" />
            新任务标记
          </label>
        </div>
      </section>

      {/* 任务步骤 */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-gray-900">任务步骤</h2>
          <button onClick={addStep} disabled={hasSentMessageItem} className="text-sm text-primary hover:underline disabled:text-gray-400 disabled:no-underline">+ 添加步骤</button>
        </div>

        {steps.length === 0 && <p className="text-sm text-gray-400">暂无步骤，点击右上方添加。</p>}

        {hasSentMessageItem ? (
          <div className="space-y-2">
            {steps.map((step, idx) => (
              <div key={step.id} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="text-xs text-gray-500">步骤 {idx + 1}</div>
                <div className="mt-1 text-sm font-medium text-gray-900">{step.title || '未命名步骤'}</div>
              </div>
            ))}
          </div>
        ) : (
          steps.map((step, idx) => (
            <TaskStepEditor
 allowVideo={!isMessageSend}
 onUploadingChange={busy=>setStepUploads(current=>({...current,[step.id]:busy}))}
              key={step.id}
              step={step}
              index={idx}
              total={steps.length}
              inputClassName={inputCls}
              onUpdate={(patch) => updateStep(idx, patch)}
              onMove={(dir) => moveStep(idx, dir)}
              onRemove={() => removeStep(idx)}
            />
          ))
        )}
        {hasSentMessageItem && <p className="text-xs text-gray-500">已有发送遥测，任务步骤暂不允许编辑，避免破坏已领取号码/正文的审核快照。</p>}
      </section>

      {/* 下载任务 App 信息 */}
      {form.task_type === 'normal' && (
        <section className="space-y-3">
          <h2 className="text-base font-medium text-gray-900">领取时访问应用（可选）</h2>
          <p className="text-sm text-gray-500">领取成功后，App 在后台访问一次指定应用页面。关键词不展示给用户；留空则不访问。</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-sm text-gray-700">领取时访问关键词
              <input value={appInfo.search_keywords?.[0] ?? ''} maxLength={100} onChange={(e) => setAppInfo((prev) => ({ ...prev, search_keywords: [e.target.value], search_keyword_quotas: [] }))} className={inputCls} placeholder="可选，例如：抖音" />
            </label>
            <label className="space-y-1 text-sm text-gray-700">App Store 应用 ID
              <input type="number" min="1" step="1" value={appInfo.id || ''} onChange={(e) => setAppInfo((prev) => ({ ...prev, id: Number(e.target.value) }))} className={inputCls} placeholder="例如：1142110895" />
            </label>
          </div>
        </section>
      )}

      {isDownload && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-medium text-gray-900">应用信息（下载任务）</h2>
            <div className="flex items-center gap-2">
              <select
                value={lookupCountry}
                onChange={(e) => setLookupCountry(e.target.value)}
                className={selectCls}
              >
                <option value="cn">中国大陆</option>
                <option value="us">美国</option>
              </select>
              <button
                onClick={handleLookup}
                disabled={lookingUp}
                className="text-sm text-primary hover:underline disabled:opacity-50 flex items-center gap-1.5"
              >
                {lookingUp && <span className="h-3 w-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />}
                从 App Store 查询
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">App ID</label>
              <input type="number" value={appInfo.id || ''} onChange={(e) => setAppField('id', parseInt(e.target.value, 10) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Bundle ID</label>
              <input type="text" value={appInfo.bundle_id} onChange={(e) => setAppField('bundle_id', e.target.value)} placeholder="com.example.app" className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">应用名称</label>
              <input type="text" value={appInfo.name} onChange={(e) => setAppField('name', e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">版本</label>
              <input type="text" value={appInfo.version} onChange={(e) => setAppField('version', e.target.value)} placeholder="1.0.0" className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">价格</label>
              <input type="number" min="0" step="0.01" value={appInfo.price || ''} onChange={(e) => setAppField('price', parseFloat(e.target.value) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">显示价格</label>
              <input type="text" value={appInfo.formatted_price} onChange={(e) => setAppField('formatted_price', e.target.value)} placeholder="¥0.00 / 免费" className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">开发者</label>
              <input type="text" value={appInfo.developer_name} onChange={(e) => setAppField('developer_name', e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">分类</label>
              <input type="text" value={appInfo.genre} onChange={(e) => setAppField('genre', e.target.value)} className={inputCls} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">文件大小(bytes)</label>
              <input type="number" min="0" value={appInfo.file_size_bytes || ''} onChange={(e) => setAppField('file_size_bytes', parseInt(e.target.value, 10) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">评分</label>
              <input type="number" min="0" max="5" step="0.1" value={appInfo.rating || ''} onChange={(e) => setAppField('rating', parseFloat(e.target.value) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">评分数</label>
              <input type="number" min="0" value={appInfo.rating_count || ''} onChange={(e) => setAppField('rating_count', parseInt(e.target.value, 10) || 0)} className={`${inputCls} tabular-nums`} />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">内容分级</label>
              <input type="text" value={appInfo.content_rating} onChange={(e) => setAppField('content_rating', e.target.value)} placeholder="4+" className={inputCls} />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">图标 URL</label>
            <input type="text" value={appInfo.artwork_url} onChange={(e) => setAppField('artwork_url', e.target.value)} placeholder="https://..." className={inputCls} />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">应用描述</label>
            <textarea value={appInfo.description} onChange={(e) => setAppField('description', e.target.value)} rows={3} className={textareaCls} />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">搜索关键词配额</label>
              <button onClick={addKeywordQuota} className="text-sm text-primary hover:underline">+ 添加关键词</button>
            </div>
            <div className="space-y-2">
              {(appInfo.search_keyword_quotas ?? []).map((item, index) => (
                <div key={index} className="grid grid-cols-[minmax(0,1fr)_120px_120px_80px] gap-2">
                  <input
                    type="text"
                    value={item.keyword}
                    onChange={(e) => updateKeywordQuota(index, { keyword: e.target.value })}
                    placeholder="关键词"
                    className={inputCls}
                  />
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={item.target_count || 1}
                    onChange={(e) => updateKeywordQuota(index, { target_count: parseInt(e.target.value, 10) || 1 })}
                    className={`${inputCls} tabular-nums`}
                  />
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={item.completed_count || 0}
                    onChange={(e) => updateKeywordQuota(index, { completed_count: parseInt(e.target.value, 10) || 0 })}
                    className={`${inputCls} tabular-nums`}
                  />
                  <button
                    onClick={() => removeKeywordQuota(index)}
                    className="h-9 rounded-md border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                  >
                    删除
                  </button>
                </div>
              ))}
            </div>
            {(appInfo.search_keyword_quotas ?? []).length === 0 && (
              <p className="text-sm text-gray-400">暂无关键词，点击右侧添加。</p>
            )}
            <p className="text-xs text-gray-400">每行依次为关键词、目标量、已完成量；当前词完成量达到目标量后，新领取用户自动切到下一个词。</p>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">截图 URLs</label>
            <textarea
              value={(appInfo.screenshot_urls ?? []).join('\n')}
              onChange={(e) => setAppField('screenshot_urls', e.target.value.split('\n').filter(Boolean))}
              rows={3}
              placeholder="每行一个 URL"
              className={textareaCls}
            />
            <p className="text-xs text-gray-400">每行一个截图链接</p>
          </div>
        </section>
      )}

      {isMessageSend && (
        <>
          <MessageImportPanel
            taskId={taskId}
            initialBatch={messageImportBatch}
            onCommitted={async () => {
              setMessageStatsRefreshKey((value) => value + 1);
              await reload();
            }}
          />
          <MessagePoolStats
            taskId={taskId}
            refreshKey={messageStatsRefreshKey}
            onStats={setFieldFromMessagePoolStats}
          />
        </>
      )}

      {/* 下载统计（仅下载任务） */}
      {form.task_type === 'download' && (
        <DownloadStatsPanel
          stats={dlStats}
          loading={dlStatsLoading}
          error={dlStatsError}
          onRetry={dlStatsReload}
        />
      )}

      {/* 相关审核记录（仅下载任务） */}
      {isDownloadTask && (
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-medium text-gray-900">相关审核记录</h2>
            <Link
              to={`/assignments?taskId=${taskId}&taskType=download`}
              className="text-sm text-primary hover:underline"
            >
              查看全部 →
            </Link>
          </div>

          {assignmentsLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="h-5 w-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
            </div>
          )}

          {assignmentsError && (
            <div className="py-4 text-center">
              <p className="text-sm text-red-500 mb-2">{assignmentsError}</p>
              <button onClick={assignmentsReload} className="text-sm text-primary hover:underline">重试</button>
            </div>
          )}

          {!assignmentsLoading && !assignmentsError && recentAssignments.length === 0 && (
            <p className="text-sm text-gray-400 py-2">暂无审核记录</p>
          )}

          {!assignmentsLoading && !assignmentsError && recentAssignments.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                    <th className="pb-2 pr-3 font-medium">ID</th>
                    <th className="pb-2 pr-3 font-medium">用户</th>
                    <th className="pb-2 pr-3 font-medium">状态</th>
                    <th className="pb-2 pr-3 font-medium">提交时间</th>
                    <th className="pb-2 pr-3 font-medium">审核时间</th>
                    <th className="pb-2 pr-3 font-medium">审核备注</th>
                    <th className="pb-2 font-medium">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAssignments.map((a) => {
                    const s = a.status as AssignmentStatus;
                    const statusCls: Record<string, string> = {
                      assigned: 'bg-blue-50 text-blue-700',
                      submitted: 'bg-amber-50 text-amber-700',
                      approved: 'bg-green-50 text-green-700',
                      rejected: 'bg-red-50 text-red-700',
                      settled: 'bg-green-50 text-green-700',
                    };
                    return (
                      <tr key={a.id} className="border-b border-gray-100 last:border-b-0">
                        <td className="py-2 pr-3 font-mono tabular-nums text-xs text-gray-600">{a.id}</td>
                        <td className="py-2 pr-3 text-gray-900 whitespace-nowrap">
                          <div>{a.user_nickname || `#${a.user_id}`}</div>
                          <div className="text-xs text-gray-400">
                            App 版本 <span className="font-mono tabular-nums">{a.latest_app_version || '-'}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusCls[s] ?? 'bg-gray-100 text-gray-600'}`}>
                            {ASSIGNMENT_STATUS_LABELS[s] ?? a.status}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-500 whitespace-nowrap tabular-nums">{a.submitted_at ? formatShortDt(a.submitted_at) : '-'}</td>
                        <td className="py-2 pr-3 text-xs text-gray-500 whitespace-nowrap tabular-nums">{a.reviewed_at ? formatShortDt(a.reviewed_at) : '-'}</td>
                        <td className="py-2 pr-3 text-xs text-gray-500 max-w-[160px] truncate" title={a.review_note || undefined}>{a.review_note || '-'}</td>
                        <td className="py-2">
                          <Link to={`/assignments/${a.id}`} className="text-xs text-primary hover:underline">查看</Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* 时间与排序 */}
      <section className="space-y-4">
        <h2 className="text-base font-medium text-gray-900">时间与排序</h2>

        <div className={timeAndSortGridClass}>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">开始时间</label>
            <input type="datetime-local" value={form.start_at} onChange={(e) => setField('start_at', e.target.value)} className={inputCls} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">结束时间</label>
            <input type="datetime-local" value={form.end_at} onChange={(e) => setField('end_at', e.target.value)} className={inputCls} />
          </div>
        </div>

        <div className="space-y-1 max-w-[200px]">
          <label className="text-sm font-medium text-gray-700">排序权重</label>
          <input type="number" min="0" step="1" value={form.sort_order} onChange={(e) => setField('sort_order', e.target.value)} className={`${inputCls} tabular-nums`} />
          <p className="text-xs text-gray-400">数值越大排序越靠前</p>
        </div>
      </section>

      {/* 操作栏 */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={!canSave || Object.values(stepUploads).some(Boolean)}
          className="px-4 py-2 text-sm rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-40 transition-colors flex items-center gap-2"
        >
          {saving && <span className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
          保存修改
        </button>
        <Link to="/tasks" className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors">
          返回列表
        </Link>
        <div className="flex-1" />
        <button
          onClick={() => setDeleteOpen(true)}
          className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50 transition-colors"
        >
          删除任务
        </button>
      </div>

      {/* Status change confirm dialog */}
      <ConfirmDialog
        open={statusTarget !== null}
        title={`确认${statusTarget ? TASK_STATUS_ACTION_LABELS[statusTarget] : ''}？`}
        description={`将任务「${task.title}」状态变更为「${statusTarget ? TASK_STATUS_LABELS[statusTarget] : ''}」。`}
        confirmLabel="确认变更"
        variant={statusTarget === 'archived' ? 'danger' : 'primary'}
        loading={statusChanging}
        onConfirm={handleStatusConfirm}
        onCancel={() => setStatusTarget(null)}
      />

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={deleteOpen}
        title="确认删除任务？"
        description={`删除「${task.title}」后不可恢复。若已有用户领取，将改为归档移除以保留领取和结算记录。`}
        confirmLabel="确认删除"
        variant="danger"
        loading={deleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  );
}
